/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * RTK Query Base Configuration
 *
 * Centralized configuration for RTK Query APIs with:
 * - Token caching integration
 * - Automatic retry for failed requests
 * - Cache invalidation strategies
 * - Error normalization
 * - Guest checkout support (no redirect on public paths)
 */

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import configuration from "@/config/configuration";
import { announceRenewalUnavailable, tokenCache } from "@/lib/token-cache";
import { logger } from "@/lib/logger";
import { redirectToAuthLogin } from "@/lib/auth-redirect";
import { mayRetryAutomatically } from "@/lib/retry-policy";

/**
 * Public paths that should NOT trigger login redirect on 401
 * These paths support guest checkout flow
 */
const PUBLIC_PATHS = [
  "/cart",
  "/add-to-cart",
  "/Basket",
  "/checkout",
  "/return",
  "/thanks",
  "/list",
  "/offline",
  "/address",
  // Unsubscribe pages -- loaded from email links by unauthenticated users.
  // Must not redirect to login on any API error.
  "/unsubscribe-cart",
  "/migration/unsubscribe",
  "/trial/unsubscribed",
  // Trial registration pages -- publicly accessible from WebView
  "/trial/register",
  "/trial/verify",
  "/trial/status",
];

/**
 * Check if current path is a public path that supports guest access
 */
const isPublicPath = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_PATHS.some(
    (publicPath) =>
      pathname.includes(publicPath) ||
      pathname.endsWith(publicPath) ||
      // Handle locale prefix (e.g., /en/cart)
      pathname.match(new RegExp(`^/[a-z]{2}${publicPath}($|/|\\?)`)),
  );
};

/**
 * Create the base query with authentication
 */
const baseQuery = fetchBaseQuery({
  baseUrl: configuration.backend_url + "/api/v1/",
  prepareHeaders: async (headers) => {
    const token = await tokenCache.getToken();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
  timeout: 30000, // 30 second timeout
});

/**
 * Per-endpoint options for the shared base query.
 *
 * S725 (LO-3): `doNotRetryStatuses` lets an endpoint opt out of the automatic
 * retry for statuses that are a deliberate answer rather than a transient
 * failure. The old-store order history answers 503 with Retry-After: 60 while
 * its circuit breaker is open; retrying that three times kept the customer on a
 * skeleton for seven seconds and spent four of their sixty hourly requests on
 * an answer that could not change.
 */
export interface BaseQueryExtraOptions {
  doNotRetryStatuses?: readonly number[];
  /**
   * N-8 (FCR S732): allow the automatic retry for a non-idempotent request.
   * Nothing sets it today; see mayRetryAutomatically in lib/http-client.ts.
   */
  retryUnsafe?: boolean;
}

/**
 * Enhanced base query with token refresh handling and retry logic
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const startTime = performance.now();
  const maxRetries = 3;
  let attempt = 0;
  let result = await baseQuery(args, api, extraOptions);

  // Retry logic for transient errors
  const doNotRetry = extraOptions?.doNotRetryStatuses ?? [];
  // N-8 (FCR S732): the same rule as the axios client -- an automatic retry
  // only for a request that does the same thing however many times it runs. A
  // refund request sent twice because its first answer was slow is exactly
  // what must not happen.
  const requestMethod = typeof args === "string" ? "GET" : args.method || "GET";
  const retryAllowed = mayRetryAutomatically({
    method: requestMethod,
    retryUnsafe: extraOptions?.retryUnsafe,
  });
  while (
    retryAllowed &&
    result.error &&
    attempt < maxRetries &&
    !(
      typeof result.error.status === "number" &&
      doNotRetry.includes(result.error.status)
    ) &&
    (result.error.status === "FETCH_ERROR" ||
      (typeof result.error.status === "number" && result.error.status >= 500))
  ) {
    attempt++;
    const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
    logger.warn(
      `API request failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms`,
    );
    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await baseQuery(args, api, extraOptions);
  }

  // Handle 401 - token expired
  if (result.error && result.error.status === 401) {
    // On public paths, don't redirect - allow guest checkout
    if (isPublicPath()) {
      logger.debug("401 on public path - allowing guest access");
      return result;
    }

    logger.warn("Token expired, attempting refresh");

    /*
     * N1 (FCR S687): a failed renewal must not destroy the refresh token.
     *
     * This handler used to treat EVERY unsuccessful outcome the same way --
     * destroy both tokens and redirect. refreshToken() returns null for a
     * rejected refresh token, a service outage, a network failure, a rate
     * limit AND the thirty-second client throttle, and no caller could tell
     * them apart. shouldClearTokensForRefreshStatus() exists so a 500, a 503,
     * a 429 or an unreachable service leaves both tokens in place -- but that
     * rule protects the REFRESH path, not this one.
     *
     * The throttle case is the sharpest: a renewal fails with a 503, a second
     * request 401s within thirty seconds, the throttle answers null because
     * the access token has expired, and a perfectly good thirty-day credential
     * is destroyed by a rate limiter added to protect the service.
     *
     * renewSession() answers with the three cases instead. Only "signed-out"
     * -- 400/401/403, the credential is genuinely no good -- redirects.
     *
     * tokenCache.clearCache() is NOT called before this any more. It resets
     * renewalAttempted to false, which puts shouldHoldForRenewal() back into
     * "hold" and can hand the route guards a spinner while the request that
     * caused all this is still unwinding.
     *
     * There are TWO of these handlers -- this one and the other in the file
     * named below -- and fixing only one leaves the defect fully live. Check
     * them against each other whenever either changes; two copies of one rule
     * is what allowed this to be described as living in a single file.
     */
    // The sibling handler is in frontend/src/lib/http-client.ts.
    const outcome = await tokenCache.renewSession();

    if (outcome.status === "renewed") {
      // Retry with the new token.
      result = await baseQuery(args, api, extraOptions);
    } else if (outcome.status === "signed-out") {
      // Issue 1 (FCR Pass 10): use the shared helper -- clears stale token,
      // validates same-origin, uses configurable path.
      logger.error("The session credential was rejected; redirecting to login");
      redirectToAuthLogin(window.location.href);
    } else {
      // "unavailable": return the 401 to the caller with the session INTACT.
      // The panel that made this request shows its own error; A5's banner tells
      // the customer they are still signed in and that it will retry.
      logger.warn("Session renewal unavailable; keeping the session");
      announceRenewalUnavailable();
    }
  }

  // Log API timing
  const duration = Math.round(performance.now() - startTime);
  const url = typeof args === "string" ? args : args.url;
  const method = typeof args === "string" ? "GET" : args.method || "GET";

  if (result.error) {
    const status =
      typeof result.error.status === "number" ? result.error.status : 0;
    logger.api(method, url, status, duration);
  } else {
    logger.api(method, url, 200, duration);
  }

  return result;
};

/**
 * Default cache times (in seconds)
 */
export const CACHE_TIMES = {
  // Short-lived data that changes frequently
  SHORT: 60, // 1 minute

  // Medium-lived data
  MEDIUM: 5 * 60, // 5 minutes

  // Long-lived data that rarely changes
  LONG: 30 * 60, // 30 minutes

  // Very stable data (product names, etc.)
  STABLE: 60 * 60, // 1 hour
} as const;

/**
 * Common tag types for cache invalidation
 */
export const TAG_TYPES = {
  ORDERS: "Orders" as const,
  PRODUCTS: "Products" as const,
  LICENSES: "Licenses" as const,
  PROFILE: "Profile" as const,
  SUBSCRIPTIONS: "Subscriptions" as const,
  INVOICES: "Invoices" as const,
  DT_CREDITS: "DtCredits" as const,  // S428: Document Translator word credits
  WORD_BALANCE: "WordBalance" as const,  // S585: TSL word balance (PR-2/PR-5)
  LEGACY_ORDERS: "LegacyOrders" as const,  // S725: old-store (AbleCommerce) order history
};
